import React from 'react';
import sortBy from 'lodash/sortBy';
import { Box, Flex, Img, Tooltip, Spinner, Text } from 'pounceV2';
import { DESTINATIONS } from 'Source/constants';
import { Destination } from 'Generated/schema';
import { Link as RRLink } from 'react-router-dom';
import urls from 'Source/urls';
import LimitItemDisplayV3 from 'Components/LimitItemDisplay/LimitItemDisplayV3';
import { useTheme } from '@mui/material';

const LOGO_SIZE = 18;

const getLogo = (
  { outputType, outputId }: Pick<Destination, 'outputType' | 'outputId'>,
  lightMode: boolean,
) => {
  const { logo, logoLight } = DESTINATIONS[outputType];
  return (
    <Img
      key={outputId}
      alt={`${outputType} logo`}
      src={lightMode && logoLight ? logoLight : logo}
      nativeWidth={LOGO_SIZE}
      nativeHeight={LOGO_SIZE}
    />
  );
};

interface RelatedDestinationsSectionProps {
  destinations: Pick<Destination, 'outputType' | 'outputId' | 'displayName'>[];
  loading: boolean;
  verbose?: boolean;
  limit?: number;
  noDestinationsText?: string;
}

const RelatedDestinationsV3: React.FC<RelatedDestinationsSectionProps> = ({
  destinations,
  loading,
  verbose = false,
  limit = 3,
  noDestinationsText = 'No destinations configured',
}) => {
  const { palette } = useTheme();
  const lightMode = palette.mode === 'light';
  const sortedDestinations = React.useMemo(
    () => sortBy(destinations, d => d.outputType),
    [destinations],
  );

  if (loading) {
    return (
      <Box height={LOGO_SIZE}>
        <Spinner size="small" />
      </Box>
    );
  }

  if (!sortedDestinations.length) {
    return (
      <Text variant="body/lg" fontWeight="medium">
        {noDestinationsText}
      </Text>
    );
  }

  // If component is verbose, we should render all destinations as row with the name of destination displayed
  if (verbose) {
    return (
      <RRLink to={urls.configure.alertDestinations.list()}>
        <Flex inline direction="column" gap={2}>
          <LimitItemDisplayV3 limit={limit}>
            {sortedDestinations.map(destination => (
              <Flex key={destination.outputId} align="center" gap={2}>
                {getLogo(destination, lightMode)}
                <Box as="span">{destination.displayName}</Box>
              </Flex>
            ))}
          </LimitItemDisplayV3>
        </Flex>
      </RRLink>
    );
  }

  return (
    <Flex align="center" gap={2}>
      <LimitItemDisplayV3 limit={limit}>
        {sortedDestinations.map(d => (
          <Tooltip key={d.outputId} content={d.displayName} placement="top">
            {getLogo(d, lightMode)}
          </Tooltip>
        ))}
      </LimitItemDisplayV3>
    </Flex>
  );
};

export default React.memo(RelatedDestinationsV3);
